import { F, getIntlTranslation } from '@/utils/intl';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Empty, Tooltip, Tree } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { EmailDocument } from './document';
import { parseSlotMarker } from './geometry';
import { LayerNode, expandableKeys, layersTreeData, slotNodeKey } from './layers';
import { Registry } from './registry';

import styles from './LayersTree.less';

interface LayersTreeProps {
  document: EmailDocument;
  registry: Registry;
  labels?: Record<string, string>;
  selectedBlockId: string | null;
  /** Which slot of the selected container is aimed at, if any. */
  selectedSlot?: number | null;
  /** `<blockId>:<slot>` for slots the canvas actually rendered; see LayersContext. */
  renderedSlots?: ReadonlySet<string>;
  onSelectBlock: (blockId: string | null) => void;
  /** A slot node was picked: its container becomes the selection. */
  onSelectSlot: (parentId: string, slot: number) => void;
}

/**
 * The document's structure, and the only way to reach blocks the canvas cannot
 * offer — an empty container, a block fully covered by a child, or one hidden by
 * a visibility condition the current sample data resolves to false.
 */
const LayersTree: React.FC<LayersTreeProps> = ({
  document,
  registry,
  labels,
  selectedBlockId,
  selectedSlot,
  renderedSlots,
  onSelectBlock,
  onSelectSlot,
}) => {
  const intl = useIntl();

  const nodes: LayerNode[] = useMemo(
    () =>
      layersTreeData(document, {
        registry,
        labels,
        renderedSlots,
        unknownLabel: getIntlTranslation(intl, {
          id: 'pages.emailCenter.visual.layers.unknownBlock',
          defaultMessage: 'Unknown block',
        }),
      }),
    [document, registry, labels, renderedSlots, intl],
  );

  const expandable = useMemo(() => expandableKeys(nodes), [nodes]);

  /**
   * Expansion is controlled, and **new** branches are expanded once as they
   * appear — a container hides exactly the blocks the canvas can't reach either,
   * so it must not open collapsed. Collapsing one afterwards sticks.
   *
   * The obvious shortcut — remounting the tree with `key={expandable.join('|')}`
   * so `defaultExpandedKeys` re-applies — is what this replaces. It cost focus:
   * clicking a tree node leaves `document.activeElement` inside the tree, and the
   * remount on the next document change destroyed that element, dropping focus to
   * `<body>`. The editor's keyboard shortcuts then went nowhere, so ⌘Z worked once
   * and ⇧⌘Z after it silently did nothing (observed in the browser).
   */
  const [expandedKeys, setExpandedKeys] = useState<string[]>(expandable);
  const seenRef = useRef(new Set(expandable));

  useEffect(() => {
    const fresh = expandable.filter((key) => !seenRef.current.has(key));
    if (fresh.length === 0) return;
    fresh.forEach((key) => seenRef.current.add(key));
    setExpandedKeys((current) => [...current, ...fresh]);
  }, [expandable]);

  /**
   * Titles, plus a warning on any slot whose component does not render it. Such a
   * slot holds blocks that Twig discards, so the email silently ships without
   * them — the user has to be told, not just allowed to notice.
   */
  const decorated = useMemo(() => {
    const orphanTitle = getIntlTranslation(intl, {
      id: 'pages.emailCenter.visual.layers.orphanedSlot',
      defaultMessage:
        'This part is not shown with the current settings, so its content would be left out of the email.',
    });

    const decorate = (list: LayerNode[]): LayerNode[] =>
      list.map((node) => ({
        ...node,
        title: node.orphaned ? (
          <Tooltip title={orphanTitle}>
            <span className={styles.orphaned}>
              <ExclamationCircleOutlined /> {node.title}
            </span>
          </Tooltip>
        ) : (
          node.title
        ),
        children: node.children ? decorate(node.children) : undefined,
      })) as never as LayerNode[];

    return decorate(nodes);
  }, [nodes, intl]);

  if (nodes.length === 0) {
    return (
      <div className={styles.layers}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <F
              id="pages.emailCenter.visual.layers.empty"
              defaultMessage="This email is empty. Add a block to start."
            />
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.layers}>
      <Tree
        blockNode
        expandedKeys={expandedKeys}
        onExpand={(keys) => setExpandedKeys(keys.map(String))}
        // A slot node shows as selected so "insert goes here" is visible; the
        // block itself stays selected underneath, which is what the inspector
        // and the canvas outline follow.
        selectedKeys={
          selectedBlockId
            ? selectedSlot != null
              ? [slotNodeKey(selectedBlockId, selectedSlot)]
              : [selectedBlockId]
            : []
        }
        treeData={decorated as never}
        onSelect={(keys, info) => {
          // AntD toggles selection, so clicking the selected node reports no
          // keys. A structure tree should not deselect on re-click — it blanks
          // the inspector for the block you just aimed at. Escape and a click on
          // empty canvas are the ways to deselect.
          if (keys.length === 0) return;
          const key = String(keys[0]);

          // Slot keys are `<blockId>:<slotIndex>`; picking one aims the next
          // insert at that column, which is the only way to fill columns 2 and 3
          // without a mouse.
          const node = info.node as unknown as LayerNode;
          if (!node.blockId) {
            const marker = parseSlotMarker(key);
            if (marker) onSelectSlot(marker.parentId, marker.slot);
            return;
          }

          onSelectBlock(key);
        }}
      />
    </div>
  );
};

export default React.memo(LayersTree);
